import React, { useState, useRef, useEffect } from "react";
import { useOnlineUsers } from "@/hooks/useOnlineUsers";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Circle, ChevronDown, ChevronUp } from "lucide-react";
import { useDirectMessageContext } from "@/context/DirectMessageContext";
import { useAuth } from "@/context/AuthContext";

const OnlineUsersStickyBar = () => {
  const { data: onlineUsers } = useOnlineUsers();
  const { user } = useAuth();
  const { openChat } = useDirectMessageContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsExpanded(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter out current user
  const filteredUsers = onlineUsers?.filter(u => u.medewerker_id !== user?.id) || [];

  if (!filteredUsers.length) return null;

  const getInitials = (name: string) =>
    name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  const handleUserClick = (u: typeof filteredUsers[0]) => {
    openChat(u.medewerker_id, u.username, u.avatar_url);
    setIsExpanded(false);
  };

  return (
    <div ref={ref} className="fixed bottom-4 right-4 z-50">
      {isExpanded && (
        <div className="mb-2 bg-popover border rounded-lg shadow-lg p-3 w-56 max-h-64 overflow-y-auto">
          <p className="text-xs font-medium text-muted-foreground mb-2">Online gebruikers</p>
          <div className="space-y-1">
            {filteredUsers.map((u) => (
              <button
                key={u.medewerker_id}
                onClick={() => handleUserClick(u)}
                className="flex items-center gap-2 w-full p-1.5 rounded-md hover:bg-accent transition-colors text-left"
              >
                <div className="relative">
                  <Avatar className="h-7 w-7">
                    {u.avatar_url ? <AvatarImage src={u.avatar_url} alt={u.username} /> : null}
                    <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                      {getInitials(u.username)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-green-500 border-2 border-popover" />
                </div>
                <span className="text-sm text-foreground truncate">{u.username}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 bg-popover border rounded-full shadow-lg px-3 py-2 hover:bg-accent transition-colors"
      >
        <Circle className="h-2.5 w-2.5 fill-green-500 text-green-500" />
        <div className="flex -space-x-1.5">
          {filteredUsers.slice(0, 4).map((u) => (
            <Avatar key={u.medewerker_id} className="h-6 w-6 border-2 border-popover">
              {u.avatar_url ? <AvatarImage src={u.avatar_url} alt={u.username} /> : null}
              <AvatarFallback className="text-[9px] bg-primary/10 text-primary">
                {getInitials(u.username)}
              </AvatarFallback>
            </Avatar>
          ))}
        </div>
        <span className="text-xs text-muted-foreground">{filteredUsers.length} online</span>
        {isExpanded ? (
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        ) : (
          <ChevronUp className="h-3 w-3 text-muted-foreground" />
        )}
      </button>
    </div>
  );
};

export default OnlineUsersStickyBar;
